import React, { ChangeEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router-dom';

import { Failure, Member, Team, TeamMember } from '../../../../../types/entity.types';
import { TableRowData } from '../../../../../types/common.types';

import { ARCHIVED } from '../../../../../utils/constants';
import { getClassNames } from '../../../../../utils/util';

import Table from '../../../../common/table/Table';
import FilePicker from '../../../../common/input/file-picker/FilePicker';
import Button from '../../../../common/button/Button';
import AddTeamModal from '../team-modals/add-team-modal/AddTeamModal';
import EditTeamModal from '../team-modals/edit-team-modal/EditTeamModal';
import AddTeamMemberModal from '../team-modals/add-team-member-modal/AddTeamMemberModal';
import RemoveTeamMemberModal from '../team-modals/remove-team-member-modal/RemoveTeamMemberModal';
import TeamUploadFailureModal from '../team-modals/team-upload-failure-modal/TeamUploadFailureModal';

import './team-table.scss';

type TeamDataTemplate = {
  teamName: string;
  numberOfMembers: string;
};

type TeamMembersDataTemplate = {
  teamMember: string;
  userName: string;
};

const TEAMS_DATA_TEMPLATE: TeamDataTemplate = {
  teamName: '',
  numberOfMembers: ''
};

const TEAMS_HIDDEN_COLUMNS: string[] = ['externalReference'];

const TEAM_MEMBERS_DATA_TEMPLATE: TeamMembersDataTemplate = {
  teamMember: '',
  userName: ''
};

const TEAM_MEMBERS_HIDDEN_COLUMNS: string[] = ['memberExternalReference'];

type Props = {
  team?: Team;
  teams: Team[];
  members: Member[];
  createTeam: (team: Team, onSuccess: () => void) => void;
  updateTeam: (team: Team, onSuccess: () => void) => void;
  deleteTeam: (onSuccess: () => void) => void;
  retrieveUnassignedMembers: () => void;
  addMemberToTeam: (memberExternalReference: string, close: () => void) => void;
  removeMemberFromTeam: (memberExternalReference: string, p: () => void) => void;
  uploadTeamExcel: (file: File, onError: (value: Failure[]) => void) => void;
  downloadTeamExcel: () => void;
};

const TeamTable = ({
  team,
  teams,
  members,
  createTeam,
  updateTeam,
  deleteTeam,
  retrieveUnassignedMembers,
  addMemberToTeam,
  removeMemberFromTeam,
  uploadTeamExcel,
  downloadTeamExcel
}: Props): ReactElement => {
  const [showAddTeamModal, setShowAddTeamModal] = useState<boolean>(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState<boolean>(false);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState<boolean>(false);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | undefined>(undefined);
  const [teamUploadFailures, setTeamUploadFailures] = useState<Failure[] | undefined>(undefined);

  const fileRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  const { status }: Readonly<Params> = useParams();

  const navigate: NavigateFunction = useNavigate();

  useEffect((): void => {
    if (showAddTeamModal || showAddTeamMemberModal) {
      retrieveUnassignedMembers();
    }
  }, [showAddTeamModal, showAddTeamMemberModal, retrieveUnassignedMembers]);

  const sortedTeams: Team[] = useMemo(
    (): Team[] =>
      [...teams].sort((teamOne: Team, teamTwo: Team): number => {
        if (!teamOne.teamName || !teamTwo.teamName) return 0;

        if (teamOne.teamName > teamTwo.teamName) return 1;
        if (teamOne.teamName < teamTwo.teamName) return -1;
        return 0;
      }),
    [teams]
  );

  const sortedMembers: TeamMember[] = useMemo((): TeamMember[] => {
    if (!team) return [];
    const emptyArray: TeamMember[] = [];

    return [...((team?.teamMembers as TeamMember[]) ?? emptyArray)].sort(
      (memberOne: TeamMember, memberTwo: TeamMember): number => {
        if (!memberOne.teamMember && !memberTwo.teamMember) {
          return 0;
        }

        if (!memberOne.teamMember) {
          return -1;
        }

        if (!memberTwo.teamMember) {
          return 1;
        }

        if (memberOne.teamMember.toLowerCase() > memberTwo.teamMember.toLowerCase()) return 1;
        if (memberOne.teamMember.toLowerCase() < memberTwo.teamMember.toLowerCase()) return -1;
        return 0;
      }
    );
  }, [team]);

  const handleDeleteClick = (): void => {
    if (!memberToRemove?.memberExternalReference) return;
    removeMemberFromTeam(memberToRemove?.memberExternalReference, (): void => setMemberToRemove(undefined));
  };

  const renderTopActions = (): ReactElement => {
    return (
      <div className="team-table__top-actions">
        {team ? (
          <p className="team-table__top-actions__message team-table__top-actions__message--team">{team.teamName}</p>
        ) : (
          <p className="team-table__top-actions__message">Click on a team to see members</p>
        )}
      </div>
    );
  };

  const renderBottomActions = (): ReactElement => {
    if (team) {
      return (
        <div className="team-table__bottom-actions">
          <Button text="Edit Team" onClick={(): void => setShowEditTeamModal(true)} />
          <Button text="Add Team Member" onClick={(): void => setShowAddTeamMemberModal(true)} />
        </div>
      );
    }

    return (
      <div className="team-table__bottom-actions">
        {status !== ARCHIVED && (
          <>
            <Button text="Add Team" onClick={(): void => setShowAddTeamModal(true)} />
            <FilePicker
              triggerElement={<Button text="Upload .xlsx" onClick={(): void => fileRef?.current?.click()} />}
              fileRef={fileRef}
              onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
                target.files?.[0] && uploadTeamExcel(target.files[0], setTeamUploadFailures)
              }
            />
          </>
        )}
        <Button text="Download .xlsx" onClick={downloadTeamExcel} />
      </div>
    );
  };

  return (
    <>
      <section className={getClassNames('team-table', { team })} data-testid="team-table">
        {team ? (
          <Table
            data={sortedMembers}
            dataTemplate={TEAM_MEMBERS_DATA_TEMPLATE}
            hiddenColumns={TEAM_MEMBERS_HIDDEN_COLUMNS}
            rowsPerPage={10}
            onDeleteRowClick={(rowData: TableRowData): void => setMemberToRemove(rowData as TeamMember)}
            topActions={renderTopActions()}
            bottomActions={renderBottomActions()}
            filter
          />
        ) : (
          <Table
            data={sortedTeams}
            dataTemplate={TEAMS_DATA_TEMPLATE}
            hiddenColumns={TEAMS_HIDDEN_COLUMNS}
            rowsPerPage={10}
            handleRowClick={(team: TableRowData): void => navigate(team.externalReference as string)}
            topActions={renderTopActions()}
            bottomActions={renderBottomActions()}
            filter
            onDeleteRowClick={undefined}
          />
        )}
      </section>

      <AddTeamModal
        show={showAddTeamModal}
        close={(): void => setShowAddTeamModal(false)}
        members={members}
        createTeam={createTeam}
      />

      <EditTeamModal
        show={showEditTeamModal}
        close={(): void => setShowEditTeamModal(false)}
        team={team}
        updateTeam={updateTeam}
        deleteTeam={deleteTeam}
      />

      <AddTeamMemberModal
        show={showAddTeamMemberModal}
        close={(): void => setShowAddTeamMemberModal(false)}
        members={members}
        addMemberToTeam={addMemberToTeam}
      />

      <RemoveTeamMemberModal
        show={!!memberToRemove}
        close={(): void => setMemberToRemove(undefined)}
        onDeleteClick={(): void => handleDeleteClick()}
      />

      <TeamUploadFailureModal
        show={!!teamUploadFailures}
        close={(): void => setTeamUploadFailures(undefined)}
        failures={teamUploadFailures}
      />
    </>
  );
};

export default TeamTable;
